/**
 * KYPA Credential Envelope: public spec page at /kypa/instrument
 *
 * Frame: a former global-wallet product leader's trust-chain post + a senior payments-industry analyst's comment
 * ("Owning the trust decision is a business. Owning the mint is a moat.").
 *
 * This page is the merchant-facing spec. It documents the format, shows
 * example curl calls, and links to the live verify endpoint.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import MintVerifySandbox from '@/components/MintVerifySandbox';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Zap, KeyRound, Ban, Clock, Fingerprint, Package, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function CodeBlock({ children, lang = '' }) {
  return (
    <pre className="bg-black/60 border border-white/10 rounded-lg p-4 overflow-x-auto text-xs text-slate-200 font-mono leading-relaxed">
      {lang && <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">{lang}</div>}
      <code>{children}</code>
    </pre>
  );
}

export default function KYPAInstrument() {
  const [spec, setSpec] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/kypa/credential/spec`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setSpec)
      .catch((e) => setErr(e.message));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>KYPA Credential Envelope · The instrument for agent accountability · Kojiru</title>
        <meta
          name="description"
          content="The KYPA Credential Envelope (KCE) is the instrument a merchant checks before accepting agent traffic. Un-pledged agents cannot mint one. Bypass becomes impossible, not a policy violation."
        />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-emerald-400 mb-6" data-testid="instrument-eyebrow">
            KCE v1.0 &middot; PUBLIC SPEC &middot; MERCHANT INTEGRATION
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6"
            data-testid="instrument-heading"
          >
            The instrument, not the rail.
          </motion.h1>
          <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
            A KYPA Credential Envelope is a short-lived, EIP-712 signed token an agent must
            attach to every outbound HTTP call. It cannot be minted without a live on-chain
            pledge on Base. The merchant checks one endpoint. If the credential is missing,
            expired, out-of-scope, or its underlying agent has been slashed, the request is
            refused.
          </p>
          <p className="text-slate-400 leading-relaxed max-w-3xl italic">
            &ldquo;A verifiable claim only matters if some party is obligated to reject the
            transaction that arrives without one, and today nobody is. If the agent does not
            hold a usable credential until the authority exists, bypass stops being a policy
            violation and becomes impossible.&rdquo;
            <span className="not-italic block mt-2 text-slate-500 text-sm">
              A senior payments-industry analyst (25+ years), on a former global-wallet product leader&rsquo;s
              trust-chain post, Feb 2026
            </span>
          </p>
        </div>
      </section>

      {/* Three properties this instrument delivers */}
      <section className="py-14 px-6 border-b border-white/5" data-testid="three-properties-triad">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">WHAT THIS INSTRUMENT DELIVERS</div>
          <h2 className="text-2xl sm:text-3xl font-black mb-3 leading-tight max-w-3xl">
            Three properties. All three verifiable at the merchant edge.
          </h2>
          <p className="text-sm text-slate-400 mb-8 max-w-3xl leading-relaxed">
            The three properties an enterprise architect will require of any authority-scope
            credential in production, and where each one lives in the KYPA envelope.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border border-cyan-500/20 rounded-xl p-5 bg-cyan-500/5" data-testid="property-consent">
              <div className="text-[10px] font-bold tracking-[1.6px] text-cyan-300 mb-3">01</div>
              <h3 className="text-lg font-bold text-white mb-3 leading-tight">Traceable consent</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Every credential is signed at issuance by the ACS signer and encodes the consumer
                as the <code className="text-cyan-300 text-xs">principal</code>. The consent is
                cryptographically recoverable from the signature, not inferred from a log line.
              </p>
            </div>
            <div className="border border-emerald-500/20 rounded-xl p-5 bg-emerald-500/5" data-testid="property-authority">
              <div className="text-[10px] font-bold tracking-[1.6px] text-emerald-300 mb-3">02</div>
              <h3 className="text-lg font-bold text-white mb-3 leading-tight">Bounded authority</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                The <code className="text-emerald-300 text-xs">mandate</code> block encodes max
                amount, recipient allow-list, category, and delivery target: signed into the
                credential itself. Out-of-scope requests are refused at the merchant edge before
                value moves.
              </p>
            </div>
            <div className="border border-amber-500/20 rounded-xl p-5 bg-amber-500/5" data-testid="property-evidence">
              <div className="text-[10px] font-bold tracking-[1.6px] text-amber-300 mb-3">03</div>
              <h3 className="text-lg font-bold text-white mb-3 leading-tight">Evidence-preserving execution</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Every settled request and every slash produces a signed JSON packet at{' '}
                <code className="text-amber-300 text-xs">/proofs</code>, anchored to the block hash.
                Auditable by counterparty, regulator, or underwriter without asking permission.
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-6 max-w-3xl leading-relaxed italic">
            The industry vocabulary for the three-pillar frame emerged in a September 2026
            governance-architecture comment thread, describing the properties any trustworthy
            agent must preserve. KYPA is one working instrument that delivers all three.
          </p>
        </div>
      </section>

      {/* Sherif three-frame callout */}
      <section className="py-12 px-6 border-b border-white/5 bg-amber-500/[0.03]" data-testid="chargeback-machine-callout">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-amber-400 mb-3">THE FAILURE MODE THIS INSTRUMENT PREVENTS</div>
          <p className="text-xl sm:text-2xl font-black text-white leading-tight mb-5">
            &ldquo;If you don&rsquo;t bind deterministic proof at authorization, your autonomous agent is just a chargeback machine.&rdquo;
          </p>
          <p className="text-sm text-slate-400 mb-6 max-w-3xl">
            a fintech founder writing publicly on payment-network liability, September 2026
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-300 leading-relaxed">
            <div><strong className="text-white block mb-1">The Consent Illusion.</strong> Consumers assume the software company is liable. Card-network rules say the cardholder authorized the token, so the cardholder is on the hook.</div>
            <div><strong className="text-white block mb-1">The Coming Chargeback Wave.</strong> When goodwill refunds stop, consumers call their bank. Cross the card networks&rsquo; 1% dispute threshold and the agent platform&rsquo;s merchant account gets shut down.</div>
            <div><strong className="text-white block mb-1">The Prompt-Log Defense Problem.</strong> No issuer accepts an LLM conversation log as proof of intent. &ldquo;The model hallucinated the checkout parameters&rdquo; is not a representment defense.</div>
          </div>
        </div>
      </section>

      {/* Consumer harm profile: a consumer research firm data anchor */}
      <section className="py-14 px-6 border-b border-white/5" data-testid="consumer-harm-profile">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-amber-400 mb-3">WHO THIS PROTECTS</div>
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight max-w-3xl">
            A profile of the consumer the recourse layer is built for.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4 text-sm text-slate-300 leading-relaxed">
              <p>
                The <em>A consumer research firm Banking and Payments Intelligence Report</em> for July 2026
                measures the U.S. consumer segment already substituting AI for professional
                financial guidance. <strong className="text-white">40% of Americans have asked AI
                for help with their money.</strong> 34% credit AI with helping them make smarter
                financial decisions: statistical parity with the 35% who credit their bank.
              </p>
              <p>
                That segment skews hardest among consumers under 40 and those already overextended.{' '}
                <strong className="text-white">66% of Americans are financially unhealthy.</strong>{' '}
                26% took financial distress actions during the survey month, including missing
                rent, selling personal belongings, or skipping medication.
              </p>
              <p>
                When a $100 wrong-outcome transaction lands on that consumer, the absence of a
                recourse layer is not a policy gap. It is an eviction, a rationed dose, or a
                household selling something. The KYPA envelope refuses the transaction at the
                merchant edge before it can settle. The pledge behind it makes the consumer whole
                if refusal fails.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-white/10 rounded-lg p-4 bg-slate-900/40" data-testid="stat-card-40">
                <div className="text-3xl font-black text-white leading-none mb-2">40%</div>
                <div className="text-xs text-slate-400">have asked AI for money help</div>
              </div>
              <div className="border border-white/10 rounded-lg p-4 bg-slate-900/40" data-testid="stat-card-34">
                <div className="text-3xl font-black text-white leading-none mb-2">34%</div>
                <div className="text-xs text-slate-400">credit AI &middot; 35% credit their bank</div>
              </div>
              <div className="border border-white/10 rounded-lg p-4 bg-slate-900/40" data-testid="stat-card-66">
                <div className="text-3xl font-black text-white leading-none mb-2">66%</div>
                <div className="text-xs text-slate-400">financially unhealthy</div>
              </div>
              <div className="border border-white/10 rounded-lg p-4 bg-slate-900/40" data-testid="stat-card-26">
                <div className="text-3xl font-black text-white leading-none mb-2">26%</div>
                <div className="text-xs text-slate-400">took financial distress actions this month</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-6">
            Source: a consumer research firm Banking and Payments Intelligence Report, published 27 July 2026 at jdpower.com.
          </p>
        </div>
      </section>

      {/* Five properties */}
      <section className="py-16 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">FIVE PROPERTIES</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-10 leading-tight">
            What a KYPA Credential must be.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { Icon: Fingerprint, title: 'Cryptographically signed', body: 'EIP-712 typed data, signed by the operator wallet listed in the KYPA directory. Recoverable, not spoofable.' },
              { Icon: Clock, title: 'Short-lived', body: 'TTL between 30 seconds and 1 hour. A replayed credential from yesterday cannot pass the time window check.' },
              { Icon: Ban, title: 'Refuses to mint when slashed', body: 'If the operator agent has been slashed on-chain, the mint endpoint returns 409. Bypass requires forging a signature: impossible without the operator PK.' },
              { Icon: Package, title: 'Scoped', body: 'Every envelope carries max_amount_cents, allowed_domains, and purpose. Out-of-scope requests fail verification even with a valid signature.' },
              { Icon: KeyRound, title: 'Pinned to an on-chain instrument', body: 'Envelope includes hook_address + chain_id. A credential minted for Base mainnet cannot be replayed against Sepolia (or vice versa).' },
              { Icon: Zap, title: 'Stateless to verify', body: 'One HTTP POST. No merchant-side DB, no OAuth dance, no shared secret. Signature recover + KYPA directory lookup + indexer read.' },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="bg-[#0f0f16] border border-white/10 rounded-xl p-5">
                <Icon className="w-6 h-6 text-emerald-400 mb-3" />
                <div className="text-lg font-bold text-white mb-2">{title}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wire format */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">WIRE FORMAT</div>
          <h2 className="text-3xl sm:text-4xl font-black leading-tight">One header. Two parts.</h2>
          <p className="text-slate-300 leading-relaxed">
            Every outbound HTTP request from a KYPA-pledged agent carries a single header:
          </p>
          <CodeBlock lang="http">{`X-KYPA-Credential: <base64_envelope>.<0x_signature>`}</CodeBlock>
          <p className="text-slate-300 leading-relaxed">
            The dot-separated payload decodes to a JSON envelope and its EIP-712 signature.
            The envelope is the primary type <code className="text-emerald-300">KYPACredential</code> under
            domain <code className="text-emerald-300">{'{'} name: &quot;KojiruKYPACredential&quot;, version: &quot;1&quot;, chainId {'}'}</code>.
          </p>

          <div className="text-sm text-slate-400 mb-2">Envelope JSON (canonical):</div>
          <CodeBlock lang="json">{`{
  "version": "1.0",
  "envelope_id": "e45ba1e4-57d1-447d-98e1-2377bdc5bbc5",
  "kypa_id":     "KYPA-000000-4134",
  "agent_id":    1782659884,
  "operator_wallet": "0x2877...3f53",
  "chain_id":    8453,
  "hook_address": "0x05F25418...e0874",
  "issued_at":   1739923200,
  "expires_at":  1739923500,
  "scope": {
    "max_amount_cents": 1000,
    "allowed_domains":  ["*.merchant.com"],
    "purpose":          "checkout"
  },
  "nonce": "a3f9c4d5e6f7a8b9c0d1e2f3a4b5c6d7"
}`}</CodeBlock>

          {spec?.example_wire_header && (
            <div>
              <div className="text-sm text-slate-400 mb-2">Live example (unsigned; for shape only):</div>
              <CodeBlock lang="wire">{spec.example_wire_header}</CodeBlock>
            </div>
          )}
          {err && (
            <div className="text-xs text-rose-400" data-testid="spec-fetch-error">
              Could not reach live spec endpoint ({err}). The example above still reflects v1.0 accurately.
            </div>
          )}
        </div>
      </section>

      {/* Verify flow */}
      <section className="py-16 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">MERCHANT-SIDE VERIFY</div>
          <h2 className="text-3xl sm:text-4xl font-black leading-tight">One POST. Nine checks. Yes/no.</h2>
          <p className="text-slate-300 leading-relaxed">
            The merchant plugs a single POST into their 402 middleware.
            Kojiru returns a boolean + a breakdown of every check.
          </p>

          <CodeBlock lang="curl">{`curl -X POST https://api.kojiru.com/api/kypa/credential/verify \\
  -H "Content-Type: application/json" \\
  -d '{
    "wire": "<value of X-KYPA-Credential from inbound request>",
    "requested_amount_cents": 500,
    "target_domain": "shop.merchant.com"
  }'`}</CodeBlock>

          <div className="text-sm text-slate-400 mb-2">Response shape:</div>
          <CodeBlock lang="json">{`{
  "valid": true,                    // if false, reject with 402 or 403
  "recovered_signer": "0x2877...3f53",
  "checks": [
    { "check": "wire_decode",             "passed": true },
    { "check": "shape",                   "passed": true },
    { "check": "time_window",             "passed": true },
    { "check": "scope_amount",            "passed": true },
    { "check": "scope_domain",            "passed": true },
    { "check": "signature",               "passed": true },
    { "check": "kypa_directory",          "passed": true },
    { "check": "wallet_matches_directory","passed": true },
    { "check": "on_chain_not_slashed",    "passed": true },
    { "check": "pledge_covers_amount",    "passed": true }
  ],
  "agent": {
    "kypa_id":            "KYPA-000000-4134",
    "agent_name":         "Founding Agent 1",
    "pledge_amount_usdc": 1500.0,
    "pledge_tier":        "Bronze",
    "framework":          "langchain"
  },
  "envelope": { /* ...decoded envelope... */ }
}`}</CodeBlock>

          <div className="pt-4">
            <div className="text-sm text-slate-400 mb-2">Fastest merchant integration (Node.js Express, ~10 lines):</div>
            <CodeBlock lang="javascript">{`// Reject any request without a valid KYPA credential.
app.use(async (req, res, next) => {
  const wire = req.header('X-KYPA-Credential');
  if (!wire) return res.status(402).json({ error: 'kypa_credential_required' });

  const r = await fetch('https://api.kojiru.com/api/kypa/credential/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      wire,
      requested_amount_cents: Math.round(req.body.amount_usd * 100),
      target_domain: req.hostname,
    }),
  });
  const v = await r.json();
  if (!v.valid) return res.status(402).json({ error: 'kypa_credential_invalid', checks: v.checks });
  req.kypa = v.agent;
  next();
});`}</CodeBlock>
          </div>
        </div>
      </section>

      {/* Live sandbox */}
      <section className="py-16 px-6 border-b border-white/5" data-testid="sandbox-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-emerald-400 mb-3">TRY IT LIVE · 60 SECONDS</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            Mint a real credential. Break it four ways.
          </h2>
          <p className="text-slate-400 leading-relaxed max-w-3xl mb-8">
            This sandbox uses a real KYPA profile flagged <code className="text-emerald-300 text-xs">is_demo: true</code>.
            The credential is signed server-side with a dedicated demo key so nothing about the flow is fake.
            Everything you see the merchant see below is the same JSON the live <code className="text-emerald-300 text-xs">/api/kypa/credential/verify</code> endpoint returns.
          </p>
          <MintVerifySandbox />
        </div>
      </section>

      {/* SDK / integration */}
      <section className="py-16 px-6 border-b border-white/5 bg-white/[0.02]" data-testid="sdk-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">AGENT SDK · v0.1</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            Two lines of agent-side code.
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-3xl mb-6">
            The reference SDK is a single Python file. Zero backend dependency at mint time, 
            envelopes are signed locally with your operator wallet PK. Wrappers ship for the
            three frameworks agent teams are actually using: popular agent frameworks.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-black/40 border border-white/10 rounded-lg p-4">
              <div className="text-[10px] font-bold tracking-widest text-emerald-300 mb-2">FRAMEWORK-AGNOSTIC</div>
              <pre className="text-[11px] text-slate-200 font-mono leading-relaxed overflow-x-auto">{`from kypa_agent_sdk import KypaSigner
signer = KypaSigner(
  kypa_id="KYPA-000000-4134",
  agent_id=1782659884,
  operator_private_key="0x...",
  chain_id=8453,
  hook_address="0x05F25418...e0874",
  default_max_amount_cents=5000,
)
session = signer.requests_session()
# every request now carries X-KYPA-Credential`}</pre>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-lg p-4">
              <div className="text-[10px] font-bold tracking-widest text-cyan-300 mb-2">LANGCHAIN · CREWAI · AUTOGPT</div>
              <pre className="text-[11px] text-slate-200 font-mono leading-relaxed overflow-x-auto">{`# a popular agent framework
tool = signer.wrap_langchain_tool(tool)

# a popular agent framework
tool = signer.wrap_crewai_tool(MyBuyTool())

# a popular agent framework
@signer.autogpt_command
def buy_item(url: str, amount: float): ...`}</pre>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="/downloads/kypa_agent_sdk.py" target="_blank" rel="noopener noreferrer" data-testid="sdk-download-link">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-2">
                <Download className="w-4 h-4" /> Download kypa_agent_sdk.py
              </Button>
            </a>
            <span className="text-xs text-slate-500 self-center">
              Single-file. <code className="text-slate-300">pip install eth-account requests</code> then drop into your agent.
            </span>
          </div>
        </div>
      </section>

      {/* Why this changes payments */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">WHY THIS CHANGES PAYMENTS</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-8 leading-tight">
            Enforcement at the instrument, not the rail.
          </h2>
          <div className="space-y-6 text-slate-300 leading-relaxed">
            <p>
              Every existing agentic-payments proposal: a card-issuing startup's APA, a cloud hyperscaler's agent payments product, a major credit bureau
              Agent Trust, a global super-app wallet AHA: enforces trust at the <em>rail</em>: the merchant asks a
              central authority whether an agent is trustworthy, and receives a policy answer. The
              agent can still act if it bypasses the policy.
            </p>
            <p>
              KYPA moves enforcement to the <em>instrument</em>. An un-pledged agent cannot sign
              a valid credential. A slashed agent cannot mint a new one. A scope-exceeding request
              fails verification even from a signed, legitimate agent. Bypass is not a policy
              violation. It is arithmetic.
            </p>
            <p>
              The credential is the smallest possible surface for an agentic-commerce standard:
              two lines of merchant integration, no shared secrets, no OAuth handshake, no
              directory API keys. If it becomes a standard, the accountability layer settles at
              whoever owns the mint.
            </p>
          </div>
        </div>
      </section>

      {/* Try it */}
      <section className="py-16 px-6 bg-gradient-to-b from-white/[0.02] to-emerald-950/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-4 leading-tight">
            Ready to try it against the live protocol?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            The verify endpoint is public. The spec endpoint returns the exact typed-data domain
            your signer library needs.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={`${API}/api/kypa/credential/spec`} target="_blank" rel="noopener noreferrer" data-testid="instrument-spec-link">
              <Button variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                <ShieldCheck className="w-4 h-4 mr-2" /> GET /api/kypa/credential/spec
              </Button>
            </a>
            <Link to="/kypa/directory" data-testid="instrument-directory-link">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                Browse KYPA Directory
              </Button>
            </Link>
            <Link to="/proofs" data-testid="instrument-proofs-link">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                See real slash proofs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
